import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, Sparkles, AlertTriangle, Lock } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * TalentOnboardingPage — página pública (sin auth) donde una nueva incorporación
 * (ej. Beca) rellena sus datos básicos protegida por un código de 4 dígitos.
 *
 * Ruta: /onboarding/talent/:token
 */
export default function TalentOnboardingPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    code: "",
    nombre: "",
    email: "",
    telefono: "",
    fecha_nacimiento: "",
    universidad: "",
    grado: "",
    linkedin_url: "",
    motivacion: "",
    areas_interes: "",
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API}/talent/onboarding/${token}`);
        const j = await r.json();
        if (!alive) return;
        if (!r.ok) { setError(j.detail || `HTTP ${r.status}`); return; }
        setPrefill(j);
        setForm((f) => ({
          ...f,
          nombre: j.nombre_prefill || "",
          linkedin_url: j.linkedin_url || "",
        }));
      } catch (e) {
        if (alive) setError(String(e.message || e));
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.code.trim()) { setError("Introduce el código que te ha compartido tu manager."); return; }
    setSaving(true); setError(null);
    try {
      const r = await fetch(`${API}/talent/onboarding/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const j = await r.json();
      if (!r.ok) { setError(j.detail || `HTTP ${r.status}`); return; }
      setSubmitted(true);
    } catch (e) { setError(String(e.message || e)); }
    finally { setSaving(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white"><Loader2 className="w-8 h-8 animate-spin text-emerald-400"/></div>;
  }

  if (error && !prefill) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white p-6">
        <div className="max-w-md text-center space-y-3">
          <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto"/>
          <h1 className="text-2xl font-black">Invitación no disponible</h1>
          <p className="text-sm text-white/70">{error}</p>
          <p className="text-xs text-white/50">Pide una invitación nueva a tu manager.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-950 via-slate-900 to-slate-950 text-white p-6" data-testid="onboarding-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
          className="max-w-md text-center space-y-4"
        >
          <div className="w-20 h-20 rounded-full bg-emerald-500 text-white flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-10 h-10"/>
          </div>
          <h1 className="text-3xl font-black">¡Bienvenida al equipo!</h1>
          <p className="text-sm text-white/80">
            Hemos guardado tu ficha básica. Tu manager la revisará y te dará acceso completo a Innovation OS.
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-950 via-slate-900 to-slate-950 text-white p-6" data-testid="talent-onboarding-page">
      <div className="max-w-2xl mx-auto py-8 space-y-6">
        <div className="text-center space-y-2">
          <div className="inline-flex items-center gap-2 text-emerald-300 text-xs uppercase tracking-widest font-bold">
            <Sparkles className="w-4 h-4"/> Valoriza · Innovation OS
          </div>
          <h1 className="text-4xl font-black leading-tight">Ficha básica de incorporación</h1>
          <p className="text-sm text-white/70 max-w-md mx-auto">
            Hola{prefill?.nombre_prefill ? ` ${prefill.nombre_prefill.split(" ")[0]}` : ""}, rellena esta ficha en 3 minutos. Tu manager la usará para hacer un primer mapeo antes de tu arranque.
          </p>
          {prefill?.manager && (
            <p className="text-[11px] text-white/50">Manager: <strong className="text-white/80">{prefill.manager}</strong> · Equipo: <strong className="text-white/80">{prefill.equipo}</strong></p>
          )}
        </div>

        <form
          onSubmit={submit}
          className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur p-6 space-y-4"
          data-testid="talent-onboarding-form"
        >
          <div>
            <Label className="text-emerald-300 text-xs uppercase tracking-wide flex items-center gap-1">
              <Lock className="w-3 h-3"/> Código de invitación
            </Label>
            <Input
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              placeholder="1234"
              inputMode="numeric"
              maxLength={6}
              className="bg-white/10 border-white/20 text-white text-lg tracking-widest"
              required
              data-testid="onboarding-code"
            />
            <p className="text-[10px] text-white/50 mt-1">Te lo dará tu manager junto con este enlace.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FormField label="Nombre completo" value={form.nombre} onChange={(v) => setForm({ ...form, nombre: v })} testid="onboarding-nombre"/>
            <FormField label="Email personal" value={form.email} onChange={(v) => setForm({ ...form, email: v })} type="email" testid="onboarding-email" required/>
            <FormField label="Teléfono" value={form.telefono} onChange={(v) => setForm({ ...form, telefono: v })} testid="onboarding-telefono"/>
            <FormField label="Fecha de nacimiento" value={form.fecha_nacimiento} onChange={(v) => setForm({ ...form, fecha_nacimiento: v })} type="date" testid="onboarding-fecha-nacimiento"/>
            <FormField label="Universidad / Centro" value={form.universidad} onChange={(v) => setForm({ ...form, universidad: v })} testid="onboarding-universidad"/>
            <FormField label="Grado / Máster en curso" value={form.grado} onChange={(v) => setForm({ ...form, grado: v })} testid="onboarding-grado"/>
            <div className="sm:col-span-2">
              <FormField label="LinkedIn" value={form.linkedin_url} onChange={(v) => setForm({ ...form, linkedin_url: v })} testid="onboarding-linkedin"/>
            </div>
          </div>

          <div>
            <Label className="text-white/80 text-xs uppercase tracking-wide">Motivación e intereses</Label>
            <Textarea
              value={form.motivacion}
              onChange={(e) => setForm({ ...form, motivacion: e.target.value })}
              rows={3}
              placeholder="¿Qué te atrae del área de innovación? Tecnologías, sectores, retos que te motivan…"
              className="bg-white/10 border-white/20 text-white"
              data-testid="onboarding-motivacion"
            />
          </div>

          <div>
            <Label className="text-white/80 text-xs uppercase tracking-wide">Áreas de interés técnico (separadas por comas)</Label>
            <Input
              value={form.areas_interes}
              onChange={(e) => setForm({ ...form, areas_interes: e.target.value })}
              placeholder="IA generativa, Economía circular, IoT, Datos, Blockchain…"
              className="bg-white/10 border-white/20 text-white"
              data-testid="onboarding-areas"
            />
          </div>

          {error && (
            <div className="rounded-lg bg-red-500/15 border border-red-400/40 px-3 py-2 text-xs text-red-200 flex items-start gap-2" data-testid="onboarding-error">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5"/> {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={saving}
            className="w-full h-11 bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-sm"
            data-testid="onboarding-submit"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : null}
            Enviar mi ficha
          </Button>

          <p className="text-[10px] text-white/40 text-center">
            Solo tu manager y RRHH verán estos datos. Guardado seguro en Innovation OS.
          </p>
        </form>
      </div>
    </div>
  );
}

function FormField({ label, value, onChange, type = "text", required = false, testid }) {
  return (
    <div>
      <Label className="text-white/80 text-xs uppercase tracking-wide">{label}{required && <span className="text-emerald-400 ml-1">*</span>}</Label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="bg-white/10 border-white/20 text-white"
        data-testid={testid}
      />
    </div>
  );
}
